import * as maplibregl from "maplibre-gl";
import { Map as MlMap } from "maplibre-gl";
import type { TileLayerName } from "../types";
import { addBackgroundTileLayer, captureAndStripStyleLayers, DEFAULT_STYLE } from "./backgroundTileLayer";
import type { CapturePlan } from "./captureRegion";

/** Plafond de zoom. Une zone de moins de quelques mètres demanderait un zoom que MapLibre ne sait
 * pas tenir ; on rend alors la zone à une résolution moindre plutôt que de rendre faux.
 *
 * Défini ICI plutôt que dans `captureRegion` (qui le ré-exporte, c'est son nom public) : les deux
 * modules s'importent l'un l'autre sinon, et un cycle de valeurs entre modules ES est exactement le
 * genre de chose qui rend `undefined` un jour de refonte. */
export const MAX_CAPTURE_ZOOM = 24;

/**
 * **Une carte MapLibre hors écran, cadrée exactement sur une zone**, le temps d'une opération.
 *
 * Deux usages, une seule mise en place : la CAPTURE d'une zone en pixels
 * ({@link captureRegion}) et la LECTURE des polygones du fond vectoriel
 * ({@link queryBasemapBuildings}). Les deux ont besoin de la même chose — un style chargé, le seul
 * fond demandé, des tuiles arrivées — et une seconde implémentation de ce cycle serait la
 * meilleure façon d'obtenir deux cadrages qui divergent d'un pixel.
 *
 * La caméra est posée analytiquement (`center`/`zoom` de `planCapture`), jamais par `fitBounds` :
 * `fitBounds` ajuste au plus contraignant des deux axes et arrondit, ce qui suffirait à décaler la
 * zone de quelques pixels — invisible à l'œil, et faux dans les coordonnées produites.
 *
 * La carte est TOUJOURS détruite à la sortie, y compris si `use` lève : chaque instance retient un
 * contexte WebGL, et les navigateurs en comptent un nombre très fini.
 */
export async function withOffscreenBasemap<T>(
  plan: CapturePlan,
  basemap: TileLayerName,
  use: (map: MlMap) => T | Promise<T>,
): Promise<T> {
  const container = document.createElement("div");
  // Hors écran, mais avec une VRAIE taille : `display: none` donnerait un conteneur 0×0, donc un
  // canvas vide (et, pour une requête de features, un viewport sans la moindre tuile).
  container.style.position = "absolute";
  container.style.top = "0";
  container.style.left = "-100000px";
  container.style.width = `${plan.width}px`;
  container.style.height = `${plan.height}px`;
  container.style.pointerEvents = "none";
  document.body.appendChild(container);

  let map: MlMap | null = null;
  try {
    map = new maplibregl.Map({
      container,
      style: DEFAULT_STYLE,
      center: plan.center,
      zoom: plan.zoom,
      bearing: 0,
      pitch: 0,
      // `pixelRatio: 1` : le canvas fait exactement `plan.width × plan.height`, quelle que soit la
      // machine. Sur un écran HiDPI, le laisser libre rendrait une capture deux fois plus grande
      // que ce que le plan annonce.
      pixelRatio: 1,
      // Sans quoi `toBlob`/`drawImage` ne liraient qu'un tampon déjà vidé.
      canvasContextAttributes: { preserveDrawingBuffer: true },
      // Les tuiles apparaissent en fondu (~300 ms) : capturer pendant le fondu donnerait des tuiles
      // à demi transparentes sur le fond blanc.
      fadeDuration: 0,
      interactive: false,
      attributionControl: false,
      maxZoom: MAX_CAPTURE_ZOOM,
    });

    const instance = map;
    await once(instance, "load");
    // Même cycle que `BackgroundMap` : charger le style, capturer ses calques, les retirer, puis ne
    // remettre que le fond demandé.
    addBackgroundTileLayer(instance, basemap, captureAndStripStyleLayers(instance));
    await idle(instance);

    return await use(instance);
  } finally {
    map?.remove();
    container.remove();
  }
}

/** Au-delà, on considère que des tuiles ne viendront jamais et on rend ce qu'on a. */
const IDLE_TIMEOUT_MS = 20_000;

function once(map: MlMap, event: "load"): Promise<void> {
  return new Promise((resolve) => map.once(event, () => resolve()));
}

/** Attend que plus rien ne soit en vol ET que le rendu soit stabilisé — sans quoi on capturerait
 * des tuiles manquantes, qui deviennent d'authentiques formes blanches après quantification, et on
 * interrogerait un fond dont la moitié des bâtiments ne sont pas encore là. */
function idle(map: MlMap): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(finish, IDLE_TIMEOUT_MS);
    function finish() {
      clearTimeout(timer);
      map.off("idle", finish);
      resolve();
    }
    // On attend TOUJOURS l'événement, sans raccourci sur `loaded()`/`areTilesLoaded()` : appelé
    // juste après l'ajout des calques, dans le même tour de boucle, l'état de la carte n'a pas
    // encore basculé sur "occupée" — le raccourci répondrait donc vrai avant la première requête de
    // tuile, et on lirait une carte vide.
    map.on("idle", finish);
  });
}
